import {
  GET_PAGE_FILTER,
  IncomingFeedbackFilters,
  THREE_MONTHS_DATE_FILTER,
} from '@hotjar/feedback/domain/IncomingFeedback/filters/ResponsesFilters';
import {
  useFeedbackWidgetResults,
  useFindFeedbackWidgetIdQuery,
} from '@hotjar/feedback/domain/IncomingFeedback/query';
import type { Site } from '@hotjar/site';
import { useCallback, useEffect, useMemo } from 'react';
import type { FeedbackResponseFilters } from '../ResponsesFilters/ResponsesFilters';
import { getAverageScore } from '../Results/AverageScore';
import type { Sentiment } from './FeedbackCards';
import { EmptyFeedbackCard, ScoreFeedbackCard } from './FeedbackCards';
import { PlaceholderFeedbackCard } from './PlaceholderFeedbackCard';

type FeedbackCardState = { sentiment: Sentiment; widgetId: number } | { sentiment: 'empty' };

export type FeedbackCardSentiment = Sentiment | 'empty';

const getSentiment = (score: number): Sentiment => {
  if (score > 4) return 'love';
  if (score > 3) return 'like';
  if (score > 2) return 'neutral';
  if (score > 1) return 'dislike';
  return 'hate';
};

const useFeedbackCardState = (
  site: Site,
  filters: Partial<FeedbackResponseFilters>,
): FeedbackCardState | undefined => {
  const { data: findWidgetResult, isLoading: isWidgetIdLoading } = useFindFeedbackWidgetIdQuery(
    site,
    filters,
  );
  const { data: results, isLoading: isResultsLoading } = useFeedbackWidgetResults(
    site,
    findWidgetResult?.id,
  );

  const sentiment = useMemo(() => {
    if (results?.sentiment === undefined) return undefined;
    const score = getAverageScore(results.sentiment);
    return getSentiment(score);
  }, [results?.sentiment]);

  if (isWidgetIdLoading || isResultsLoading) return undefined;

  if (findWidgetResult === undefined || sentiment === undefined) return { sentiment: 'empty' };

  return { sentiment, widgetId: findWidgetResult.id };
};

export type FeedbackCardProps = {
  site: Site;
  pageUrl: string;
  pageOperation: 'simple_match' | 'exact';
  onClick: (score: FeedbackCardSentiment) => void;
  onRatingSet: (score: FeedbackCardSentiment) => void;
};

export const FeedbackCard = ({
  site,
  pageUrl,
  pageOperation,
  onClick,
  onRatingSet,
}: FeedbackCardProps): JSX.Element | null => {
  const filters: Partial<FeedbackResponseFilters> = {
    [IncomingFeedbackFilters.PAGE]: GET_PAGE_FILTER(pageUrl, pageOperation),
    [IncomingFeedbackFilters.DATE]: THREE_MONTHS_DATE_FILTER,
  };

  const state = useFeedbackCardState(site, filters);

  useEffect(() => {
    if (state === undefined) return;
    onRatingSet(state.sentiment);
  }, [onRatingSet, state]);

  const onEmptyStateButtonClicked = useCallback(() => onClick('empty'), [onClick]);

  if (state === undefined) return <PlaceholderFeedbackCard />;

  if (state.sentiment === 'empty') {
    return <EmptyFeedbackCard siteId={site.id} onClick={onEmptyStateButtonClicked} />;
  }

  return (
    <ScoreFeedbackCard
      siteId={site.id}
      widgetId={state.widgetId}
      sentiment={state.sentiment}
      onClick={onClick}
      pageOperation={pageOperation}
      pageUrl={pageUrl}
    />
  );
};
